import { z } from "zod";
import {
  safeGetItem,
  safeRemoveItem,
  safeSetItem,
} from "../../helpers/storage.helpers";

export const viewConfigSchema = z
  .object({
    IntegrationsEnabled: z.boolean().catch(false),
  })
  .catch({ IntegrationsEnabled: false });

// The ids and the role back every school- and organization-scoped request, so
// they are required: a user object without them cannot be repaired, only
// replaced. The presentational fields degrade to a default instead — a payload
// that renamed or dropped one of those is no reason to throw the user out.
export const sessionUserSchema = z.object({
  ID: z.number(),
  Role: z.number(),
  SchoolID: z.number(),
  OrganizationID: z.number(),
  FirstName: z.string().catch(""),
  LastName: z.string().catch(""),
  Email: z.string().catch(""),
  // 0 is already the "belongs to no group" sentinel throughout the app.
  SchoolClassID: z.number().catch(0),
  ViewConfig: viewConfigSchema,
});

export type ViewConfig = z.infer<typeof viewConfigSchema>;
export type SessionUser = z.infer<typeof sessionUserSchema>;

const userKey = "skillster.teacher.user";
const tokenKey = "authentication_token";
const schoolIdKey = "school_id";
const organizationIdKey = "organization_id";

/**
 * The only place a session is read back from storage. A stored user that does
 * not satisfy sessionUserSchema is not a session: useAuth consumers rely on
 * the ids being present, so a half-valid user would send null into
 * school-scoped requests. Drop the whole session — one login repairs it.
 *
 * No telemetry here on purpose: this runs at module init, before posthog.init
 * in main.tsx, so a captureException would be dropped anyway. The login
 * response is where a malformed payload is worth reporting.
 */
export function readStoredSession(): SessionUser | null {
  const stored = safeGetItem(userKey);
  if (!stored) return null;

  let parsed;
  try {
    parsed = sessionUserSchema.safeParse(JSON.parse(stored));
  } catch {
    // Corrupt JSON would otherwise throw on every reload, locking the app in a
    // white screen until the user manually clears site data.
    clearStoredSession();
    return null;
  }

  if (parsed.success) return parsed.data;

  clearStoredSession();
  return null;
}

export function writeStoredSession(user: SessionUser, token: string): boolean {
  // Every API request reads the token from storage — if it cannot be stored
  // the session is dead on arrival, so the caller needs to know.
  const tokenStored = safeSetItem(tokenKey, token);
  safeSetItem(userKey, JSON.stringify(user));
  safeSetItem(schoolIdKey, user.SchoolID.toString());
  safeSetItem(organizationIdKey, user.OrganizationID.toString());
  return tokenStored;
}

export function clearStoredSession() {
  safeRemoveItem(userKey);
  safeRemoveItem(tokenKey);
  safeRemoveItem(schoolIdKey);
  safeRemoveItem(organizationIdKey);
}

// A garbage value — an old "undefined" write, a tampered key — would otherwise
// become NaN, which passes every null check and poisons the query keys it ends
// up in.
function readStoredId(key: string): number | null {
  const raw = safeGetItem(key);
  if (!raw) return null;
  const id = Number(raw);
  return Number.isInteger(id) && id > 0 ? id : null;
}

export function readStoredSelectedSchoolId() {
  return readStoredId(schoolIdKey);
}

export function readStoredSelectedOrganizationId() {
  return readStoredId(organizationIdKey);
}

export function writeStoredSelectedSchoolId(id: number | null) {
  if (id) {
    safeSetItem(schoolIdKey, id.toString());
  } else {
    safeRemoveItem(schoolIdKey);
  }
}

export function writeStoredSelectedOrganizationId(id: number | null) {
  if (id) {
    safeSetItem(organizationIdKey, id.toString());
  } else {
    safeRemoveItem(organizationIdKey);
  }
}

const sessionKeys = [userKey, tokenKey, schoolIdKey, organizationIdKey];

/**
 * Calls onChange when another tab logs in, logs out or switches school or
 * organization. Requests read the token and school_id from storage while the
 * UI holds its own copy, so a tab that ignores this sends the other tab's
 * session under its own header. The browser only fires "storage" in the other
 * tabs, and only when a value actually changed.
 */
export function onOtherTabSessionChange(onChange: () => void) {
  const listener = (event: StorageEvent) => {
    // key is null when another tab cleared all of storage.
    if (event.key === null || sessionKeys.includes(event.key)) onChange();
  };
  window.addEventListener("storage", listener);
  return () => window.removeEventListener("storage", listener);
}
